import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import Dashboard from "./components/dashboard/Dashboard";
import NoPage from "./components/common/NoPage";
import Layout from "./components/common/Layout";
import PopupPage from "./components/common/PopupPage";
import LogInRoute from "./components/routes/LogInRoute";
import PrivateRoute from "./components/routes/PrivateRoute";
import RunbookGrid from "./components/runbook/RunbookGrid";
import RunbookExecutionHistoryGrid from "./components/runbook/RunbookExecutionHistoryGrid";
import RunbookExecutionHistoryLineGrid from "./components/runbook/RunbookExecutionHistoryLineGrid";
import RunbookVersionGrid from "./components/runbook/RunbookVersionGrid";
import "./custom.scss";
import "./App.css";
import Cookies from "js-cookie";
import SchedulerGrid from "./components/scheduler/SchedulerGrid";
import SignInHelp from "./components/user/SignInHelp";
import Register from "./components/user/Register";
import SchedulerForm from "./components/scheduler/SchedulerForm";
import { settingsActions } from "./actions/settings.actions";
import VariableGrid from "./components/variable/VariableGrid";
import VariableForm from "./components/variable/VariableForm";
import RunbookDetails from "./components/runbook/RunbookDetails";
import Profile from "./components/user/Profile";
import ConnectorGrid from "./components/connector/ConnectorGrid";
import ConnectorForm from "./components/connector/ConnectorForm";
import ClientGrid from "./components/client/ClientGrid";
import ClientForm from "./components/client/ClientForm";
import VariableLineForm from "./components/variable/VariableLineForm";
import BroadcastMessageGrid from "./components/broadcastMessage/BroadcastMessageGrid";
import BroadcastMessageForm from "./components/broadcastMessage/BroadcastMessageForm";
import UsersGrid from "./components/user/UsersGrid";
import UserForm from "./components/user/UserForm";
import RunbookConnectorGrid from "./components/runbook/RunbookConnectorGrid";
import BroadcastMessageReviewGrid from "./components/broadcastMessage/BroadcastMessageReviewGrid";
import InvitationsGrid from "./components/invitations/InvitationsGrid";
import RunbookForm from "./components/runbook/RunbookForm";
import RunbookTagGrid from "./components/runbook/RunbookTagGrid";
import OktaAuthWrapper from "./okta/oktaAuthWrapper";
import { oktaDefaultConfig } from "./okta/oktaDefaultConfig";
import { LoginCallback } from "@okta/okta-react";
import AuthenticatorsGrid from "./components/client/AuthenticatorGrid";
import AuthenticatorForm from "./components/client/AuthenticatorForm";
import { TourProvider } from "./contexts/TourContext";
import { DarkModeProvider } from "./contexts/DarkModeContext";
import { ToastProvider } from "./contexts/ToastContext";
import { WorkspaceExperienceProvider } from "./contexts/WorkspaceExperienceContext";
import WorkflowBuilder from "./components/workflow/WorkflowBuilder";
import PermissionsManager from "./components/permissions/PermissionsManager";
import AzureAdCallback from "./components/AzureAdCallback";
import ArmaninoSSO from "./components/product/ArmaninoSSO";
import ProductHub from "./components/product/ProductHub";
import SettingsHub from "./components/settings/SettingsHub";
import WorkspaceHub from "./components/workspace/WorkspaceHub";
import RunbookSystemGrid from "./components/runbookSystem/RunbookSystemGrid";
import RunbookSystemForm from "./components/runbookSystem/RunbookSystemForm";
import RunbookServerGrid from "./components/runbookServer/RunbookServerGrid";
import RunbookServerForm from "./components/runbookServer/RunbookServerForm";
import AzureCostGrid from "./components/azureCost/AzureCostGrid";
import McpTokenGrid from "./components/mcpToken/McpTokenGrid";
import McpUserTokens from "./components/user/McpUserTokens";
import InspectRequestGrid from "./components/inspectRequest/InspectRequestGrid";
import HelpLayout from "./components/help/HelpLayout";
import AuditLogGrid from "./components/auditlog/AuditLogGrid";
import Analytics from "./components/analytics/Analytics";
import AllClientsAnalytics from "./components/analytics/superuser/AllClientsAnalytics";
import ClientAnalyticsView from "./components/analytics/superuser/ClientAnalyticsView";
import UsageAnalytics from "./components/usageAnalytics/UsageAnalytics";
import UsageAnalyticsOverview from "./components/usageAnalytics/product/UsageAnalyticsOverview";
import "./styles/darkmode.css";

function App() {
  const dispatch = useDispatch();
  const [oktaConfigUpdated, setOktaConfigUpdated] = useState(false);

  const handleOktaDetected = (newOktaConfig) => {
    Cookies.set("oktaConfig", JSON.stringify(newOktaConfig), { expires: 30 });
    setOktaConfigUpdated(true);
  };

  let user = Cookies.get("user")
    ? JSON.parse(Cookies.get("user"))
    : undefined;

  // Inactivity warning and logout state
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [secondsUntilLogout, setSecondsUntilLogout] = useState(60);

  const forceLogout = useCallback(() => {
    // Remove all auth-related cookies with various path options to ensure removal
    Cookies.remove("user");
    Cookies.remove("user", { path: "/" });
    Cookies.remove("oktaConfig");
    Cookies.remove("oktaConfig", { path: "/" });
    // Clear last activity timestamp
    localStorage.removeItem("lastActivityTimestamp");
    // Force full page reload to login
    window.location.replace("/login");
  }, []);

  const handleStayLoggedIn = useCallback(() => {
    localStorage.setItem("lastActivityTimestamp", Date.now().toString());
    setShowInactivityWarning(false);
    setSecondsUntilLogout(60);
  }, []);

  // Inactivity-based logout + token expiration check
  useEffect(() => {
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
    const WARNING_BEFORE_LOGOUT = 60 * 1000; // Show warning 60 seconds before logout
    const CHECK_INTERVAL = 10 * 1000; // Check every 10 seconds

    // Only run if user is logged in
    if (!Cookies.get("user")) {
      return;
    }

    // Check if token is expired
    const isTokenExpired = () => {
      try {
        const userCookie = Cookies.get("user");
        if (!userCookie) return true;

        const userData = JSON.parse(userCookie);
        if (!userData.token) return true;

        const payload = JSON.parse(atob(userData.token.split(".")[1]));
        const expiration = payload.exp;
        const currentTimestamp = Math.floor(Date.now() / 1000);

        // Allow a small grace period so minor clock skew between the client
        // and the API can't make a freshly issued token look expired
        const CLOCK_SKEW_GRACE_SECONDS = 30;
        return expiration + CLOCK_SKEW_GRACE_SECONDS < currentTimestamp;
      } catch (e) {
        return true;
      }
    };

    // Initialize or get last activity timestamp
    const updateLastActivity = () => {
      localStorage.setItem("lastActivityTimestamp", Date.now().toString());
    };

    // Always reset the activity timestamp on mount so a stale value left over
    // from a previous session can't trigger an immediate inactivity logout
    // right after login (#1817 #1818)
    updateLastActivity();

    // Activity events to track (intentionally excludes mousemove - too sensitive)
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    // Throttle activity updates to avoid excessive localStorage writes
    let throttleTimer = null;
    const throttledUpdateActivity = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        updateLastActivity();
        throttleTimer = null;
      }, 1000); // Update at most once per second
    };

    // Add event listeners for user activity
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledUpdateActivity, { passive: true });
    });

    // Check for inactivity and token expiration periodically
    const checkSession = () => {
      const userCookie = Cookies.get("user");
      if (!userCookie) {
        // User already logged out
        return;
      }

      // Check if JWT token has expired (for long working sessions)
      if (isTokenExpired()) {
        forceLogout();
        return;
      }

      // Check for inactivity
      const lastActivity = parseInt(localStorage.getItem("lastActivityTimestamp") || "0", 10);
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
        // Time's up - log out
        forceLogout();
      } else if (timeSinceLastActivity >= INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT) {
        // Show warning
        const remaining = Math.ceil((INACTIVITY_TIMEOUT - timeSinceLastActivity) / 1000);
        setSecondsUntilLogout(remaining);
        setShowInactivityWarning(true);
      } else {
        // User is active, hide warning if shown
        setShowInactivityWarning(false);
        setSecondsUntilLogout(60);
      }
    };

    // Check immediately and then periodically
    checkSession();
    const intervalId = setInterval(checkSession, CHECK_INTERVAL);

    return () => {
      clearInterval(intervalId);
      if (throttleTimer) clearTimeout(throttleTimer);
      activityEvents.forEach(event => {
        document.removeEventListener(event, throttledUpdateActivity);
      });
    };
  }, [forceLogout]);

  // Countdown timer when warning is shown
  useEffect(() => {
    if (!showInactivityWarning) return;

    const countdownInterval = setInterval(() => {
      setSecondsUntilLogout(prev => {
        if (prev <= 1) {
          forceLogout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [showInactivityWarning, forceLogout]);

  // Convert string boolean values to actual booleans for backward compatibility
  // Backend returns "True"/"False" strings instead of true/false booleans
  if (user) {
    if (typeof user.isAdmin === 'string') {
      user.isAdmin = user.isAdmin.toLowerCase() === 'true';
    }
    if (typeof user.isSuperUser === 'string') {
      user.isSuperUser = user.isSuperUser.toLowerCase() === 'true';
    }
    if (typeof user.isProductTeam === 'string') {
      user.isProductTeam = user.isProductTeam.toLowerCase() === 'true';
    }
  }

  const oktaConfig = Cookies.get("oktaConfig")
    ? JSON.parse(Cookies.get("oktaConfig"))
    : oktaDefaultConfig;

  useEffect(() => {
    if (user && user.isSuperUser) dispatch(settingsActions.get());
    // eslint-disable-next-line
  }, [user]);

  return (
    <DarkModeProvider>
      <WorkspaceExperienceProvider>
      <ToastProvider>
      <TourProvider>
        <OktaAuthWrapper oktaConfig={oktaConfig}>
        <BrowserRouter>
          <Routes>
          <Route
            index
            element={
              <LogInRoute
                onOktaDetected={handleOktaDetected}
                oktaConfigUpdated={oktaConfigUpdated}
              />
            }
          />
          <Route
            path="login"
            element={
              <LogInRoute
                onOktaDetected={handleOktaDetected}
                oktaConfigUpdated={oktaConfigUpdated}
              />
            }
          />
          <Route path="callback" element={<LoginCallback />} />
          <Route path="login/callback" element={<AzureAdCallback />} />
          <Route path="sign-in-help" element={<SignInHelp />} />
          <Route path="register" element={<Register />} />
          {/* Popup targets for WorkspaceHub's popOutRunbookWindow — deliberately siblings
              of the Layout route below, not nested in it, so they render WITHOUT the header
              or sidebar: a window.open() popup should show just the report, not a second
              copy of the app's navigation chrome around it. */}
          <Route
            path="/runbook/executions/popup"
            element={
              <PrivateRoute user={user}>
                <PopupPage>
                  <RunbookExecutionHistoryGrid user={user} linesPath="/runbook/executions/lines/popup" />
                </PopupPage>
              </PrivateRoute>
            }
          />
          <Route
            path="/runbook/executions/lines/popup"
            element={
              <PrivateRoute user={user}>
                <PopupPage>
                  <RunbookExecutionHistoryLineGrid user={user} />
                </PopupPage>
              </PrivateRoute>
            }
          />
          <Route path="/" element={<Layout user={user} />}>
            <Route
              path="/profile"
              element={
                <PrivateRoute user={user}>
                  <Profile user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute user={user}>
                  <Dashboard user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <PrivateRoute user={user}>
                  <Analytics user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics/all-clients"
              element={
                <PrivateRoute user={user}>
                  <AllClientsAnalytics user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/analytics/client/:clientId"
              element={
                <PrivateRoute user={user}>
                  <ClientAnalyticsView user={user} />
                </PrivateRoute>
              }
            />
            {/* Client-scoped usage. Role gating is done inside the component, matching
                /analytics/all-clients. */}
            <Route
              path="/usage"
              element={
                <PrivateRoute user={user}>
                  <UsageAnalytics user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/usage-analytics"
              element={
                <PrivateRoute user={user} anyOf={["superUser", "productTeam"]}>
                  <UsageAnalyticsOverview user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/usage-analytics/client/:clientId"
              element={
                <PrivateRoute user={user} anyOf={["superUser", "productTeam"]}>
                  <UsageAnalyticsOverview user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook"
              element={
                <PrivateRoute user={user}>
                  <RunbookGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/executions"
              element={
                <PrivateRoute user={user}>
                  <RunbookExecutionHistoryGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/executions/lines"
              element={
                <PrivateRoute user={user}>
                  <RunbookExecutionHistoryLineGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/versions"
              element={
                <PrivateRoute user={user}>
                  <RunbookVersionGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/details"
              element={
                <PrivateRoute user={user}>
                  <RunbookDetails user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/add"
              element={
                <PrivateRoute user={user}>
                  <RunbookForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/workflow"
              element={
                <PrivateRoute user={user}>
                  <WorkflowBuilder user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/edit"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <RunbookForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/scheduler"
              element={
                <PrivateRoute user={user}>
                  <SchedulerGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/scheduler/add"
              element={
                <PrivateRoute user={user}>
                  <SchedulerForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/scheduler/edit"
              element={
                <PrivateRoute user={user}>
                  <SchedulerForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbook/connector"
              element={
                <PrivateRoute user={user}>
                  <RunbookConnectorGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/variable"
              element={
                <PrivateRoute user={user}>
                  <VariableGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/variable/add"
              element={
                <PrivateRoute user={user}>
                  <VariableForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/variable/edit"
              element={
                <PrivateRoute user={user}>
                  <VariableForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/variable/line/edit"
              element={
                <PrivateRoute user={user}>
                  <VariableLineForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/variable/line/add"
              element={
                <PrivateRoute user={user}>
                  <VariableLineForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/connector"
              element={
                <PrivateRoute user={user}>
                  <ConnectorGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/connector/edit"
              element={
                <PrivateRoute user={user}>
                  <ConnectorForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/connector/add"
              element={
                <PrivateRoute user={user}>
                  <ConnectorForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/workspace/*"
              element={
                <PrivateRoute user={user}>
                  <WorkspaceHub user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/user"
              element={
                <PrivateRoute user={user} onlyAdmin={true}>
                  <UsersGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/runbooktag"
              element={
                <PrivateRoute user={user} onlyAdmin={true}>
                  <RunbookTagGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/user/edit"
              element={
                <PrivateRoute user={user} onlyAdmin={true}>
                  <UserForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/client"
              element={
                <PrivateRoute user={user} anyOf={["superUser", "productTeam"]}>
                  <ClientGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/client/renew"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <ClientForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/client/add"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <ClientForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/client/authenticators"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <AuthenticatorsGrid />
                </PrivateRoute>
              }
            />
            <Route
              path="/client/authenticators/add"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <AuthenticatorForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/client/authenticators/edit"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <AuthenticatorForm />
                </PrivateRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <BroadcastMessageGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/messages/add"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <BroadcastMessageForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/messages/edit"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <BroadcastMessageForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/messages/reviews"
              element={
                <PrivateRoute user={user} onlySuperUser={true}>
                  <BroadcastMessageReviewGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/invitation"
              element={
                <PrivateRoute user={user} onlyAdmin={true}>
                  <InvitationsGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/permissions"
              element={
                <PrivateRoute user={user} onlyAdmin={true}>
                  <PermissionsManager user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute user={user} anyOf={["admin", "superUser", "productTeam"]}>
                  <SettingsHub user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/auditlog"
              element={
                <PrivateRoute user={user}>
                  <AuditLogGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/armanino-sso"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <ArmaninoSSO user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <ProductHub user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/systems"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <RunbookSystemGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/systems/add"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <RunbookSystemForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/systems/edit"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <RunbookSystemForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/servers"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <RunbookServerGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/servers/add"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <RunbookServerForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/servers/edit"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <RunbookServerForm user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/azure-costs"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <AzureCostGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/mcp-tokens"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <McpTokenGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/user-mcp-tokens"
              element={
                <PrivateRoute user={user} onlyProductTeam={true}>
                  <McpUserTokens user={user} scope="all" />
                </PrivateRoute>
              }
            />
            <Route
              path="/product/inspect-requests"
              element={
                <PrivateRoute user={user} anyOf={["superUser", "productTeam"]}>
                  <InspectRequestGrid user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="/help/*"
              element={
                <PrivateRoute user={user}>
                  <HelpLayout user={user} />
                </PrivateRoute>
              }
            />
            <Route
              path="*"
              element={
                <PrivateRoute user={user}>
                  <NoPage />
                </PrivateRoute>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
        </OktaAuthWrapper>

        {/* Inactivity Warning Modal */}
        <Modal
          show={showInactivityWarning}
          onHide={handleStayLoggedIn}
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header>
            <Modal.Title>Session Timeout Warning</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <div className="mb-3">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" fill="#f59e0b" viewBox="0 0 16 16">
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                  <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
                </svg>
              </div>
              <p className="mb-2">Your session is about to expire due to inactivity.</p>
              <p className="mb-3">You will be logged out in:</p>
              <h2 className="text-danger mb-3">{secondsUntilLogout} seconds</h2>
              <p className="text-muted small">Click "Stay Logged In" to continue your session.</p>
            </div>
          </Modal.Body>
          <Modal.Footer className="justify-content-center">
            <Button variant="outline-secondary" onClick={forceLogout}>
              Log Out Now
            </Button>
            <Button variant="primary" onClick={handleStayLoggedIn}>
              Stay Logged In
            </Button>
          </Modal.Footer>
        </Modal>
      </TourProvider>
      </ToastProvider>
      </WorkspaceExperienceProvider>
    </DarkModeProvider>
  );
}

export default App;
